// WIDGET 7 — Failed Control Remediation Status: links each failed control to its POA&M plan.
// Self-fetching: calls its own dedicated endpoint
// (GET /dashboard/grc/system-owner/remediation-status) rather than a shared rollup.
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, ArrowRight, Clock, CheckCircle2, Search } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList } from "recharts";
import { DataTable, type Column } from "../../../components/DataTable";
import { Card, SectionTitle } from "../../GRCManagerDashboard/grcDashboardWidgets";
import { Tip } from "../../../components/ui/tooltip";
import { Sheet, SheetContent } from "../../../components/ui/sheet";
import { EmptyState } from "../../../components/EmptyState";
import { SkeletonList } from "../../../components/Skeleton";
import {
  STATUS_HEX,
  NOT_IDENTIFIED_HEX,
  REMEDIATION_STATUS_ORDER,
  sortRemediationItems,
  buildRemediationStatus,
  type AppRemediationSummary,
  type RemediationItem,
  type RemediationStatusKey,
} from "../data/applicationKriData";
import { fetchRemediationStatus, type SystemOwnerRemediationResponse } from "../../../../api/frameworksApi";

const STATUS_META: Record<RemediationStatusKey, { hex: string; icon: typeof AlertCircle; label: string }> = {
  overdue: { hex: STATUS_HEX.serious, icon: Clock, label: "Overdue" },
  noPlan: { hex: STATUS_HEX.critical, icon: AlertCircle, label: "No Plan" },
  inRemediation: { hex: STATUS_HEX.good, icon: CheckCircle2, label: "In Remediation" },
};

function StatusBadge({ status }: { status: RemediationStatusKey }) {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-semibold" style={{ background: `${meta.hex}1f`, color: meta.hex }}>
      <Icon className="size-3" /> {meta.label}
    </span>
  );
}

function daysAgo(iso: string): number {
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 86400000));
}
function failedSinceLabel(iso: string): string {
  const d = daysAgo(iso);
  if (d === 0) return "Today";
  return `${d} day${d === 1 ? "" : "s"} ago`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/* Due-date label + color per the routing table: overdue is always red;
   in-remediation shifts amber -> green as the due date gets further out;
   no-plan (and no due date at all) is neutral grey. */
function dueInfo(item: RemediationItem): { text: string; hex: string } {
  if (item.status === "overdue") {
    const days = Math.abs(item.daysUntilDue ?? 0);
    return { text: `Overdue by ${days} day${days === 1 ? "" : "s"}`, hex: STATUS_HEX.critical };
  }
  if (item.status === "noPlan" || item.daysUntilDue === null) {
    return { text: "No due date", hex: NOT_IDENTIFIED_HEX };
  }
  if (item.daysUntilDue === 0) return { text: "Due today", hex: STATUS_HEX.serious };
  if (item.daysUntilDue <= 7) return { text: `Due in ${item.daysUntilDue} days`, hex: STATUS_HEX.serious };
  return { text: `Due in ${item.daysUntilDue} days`, hex: STATUS_HEX.good };
}

function RemediationCell({ item }: { item: RemediationItem }) {
  const d = dueInfo(item);
  return (
    <span className="text-xs font-medium" style={{ color: d.hex }}>
      {d.text}
      {item.dueDate && ` (${formatDate(item.dueDate)})`}
    </span>
  );
}

/* Per-application rollup for the chart — sorted worst-accountability-first (Overdue + No
   Plan) so the app most needing attention is on top.

   recharts' minPointSize inflates a segment's RENDERED width but does not recompute the
   START position of the next stacked segment (still placed from the true, tiny value) —
   so two boosted segments end up drawn on top of each other. Fixing that requires the
   position and the width to come from the same number, so instead of boosting at render
   time we boost the DATA value itself: any nonzero bucket smaller than `floor` (a small
   percentage of the chart's own max total) is raised to `floor` before it ever reaches
   recharts, so stacking offsets and rendered widths agree by construction. The *real*
   counts are kept in parallel Overdue/NoPlan/InRemediation fields for labels/tooltips —
   only the plotted value is exaggerated, never the number shown to the user.
   Zero-value buckets become null (not 0): recharts still draws a rect for a 0-width bar,
   whose stroke renders as a stray colored tick — null skips the segment entirely. */
// Stack order used both to build bars and to decide which segment gets the
// left/right rounded end of the whole stack (see `makeSegmentShape` below).
const STACK_ORDER = ["overdue", "noPlan", "inRemediation"] as const;

function buildAppBars(appSummaries: AppRemediationSummary[]) {
  const rows = appSummaries.map((a) => ({
    applicationName: a.applicationName,
    total: a.noPlan + a.overdue + a.inRemediation,
    accountability: a.noPlan + a.overdue, // sort key: unowned + overdue failures, worst first
    overdueReal: a.overdue,
    noPlanReal: a.noPlan,
    inRemediationReal: a.inRemediation,
  }));
  const globalMax = Math.max(1, ...rows.map((r) => r.total));
  const floor = Math.max(1, Math.round(globalMax * 0.03));
  return rows
    .map((r) => {
      const built = {
        ...r,
        overdue: r.overdueReal > 0 ? Math.max(r.overdueReal, floor) : null,
        noPlan: r.noPlanReal > 0 ? Math.max(r.noPlanReal, floor) : null,
        inRemediation: r.inRemediationReal > 0 ? Math.max(r.inRemediationReal, floor) : null,
        totalMarker: 0.0001,
      };
      const present = STACK_ORDER.filter((k) => built[k] != null);
      return {
        ...built,
        firstSegment: present[0] ?? null,
        lastSegment: present[present.length - 1] ?? null,
      };
    })
    .sort((a, b) => b.accountability - a.accountability);
}

/* Each stacked segment used to get all 4 corners rounded (radius=[4,4,4,4]), so
   adjacent segments' rounded edges overlapped at the seam and the border between
   them all but disappeared — only the outer stack ends read as distinct "edges".
   Rounding all 4 corners but INSETTING each segment by a small gap on the
   side(s) that face a neighboring segment turns each segment into its own
   separated, fully-rounded pill — both its left and right edges read clearly,
   the same look as the "17 | 140" pill pair in the reference design. */
function makeSegmentShape(key: (typeof STACK_ORDER)[number]) {
  return (props: any) => {
    const { x, y, width, height, payload, fill, stroke, strokeWidth } = props;
    if (!(width > 0) || !(height > 0)) return null;
    const isFirst = payload.firstSegment === key;
    const isLast = payload.lastSegment === key;
    const gap = 3;
    let rectX = x;
    let rectW = width;
    if (!isFirst) {
      rectX += gap / 2;
      rectW -= gap / 2;
    }
    if (!isLast) {
      rectW -= gap / 2;
    }
    if (rectW <= 0) return null;
    const r = Math.min(4, rectW / 2, height / 2);
    return <rect x={rectX} y={y} width={rectW} height={height} rx={r} ry={r} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
  };
}

function AppBarTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: { applicationName: string; total: number; overdueReal: number; noPlanReal: number; inRemediationReal: number } }>;
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <p className="font-semibold text-foreground">{p.applicationName}</p>
      <p className="text-muted-foreground">{p.total} failed control{p.total === 1 ? "" : "s"}</p>
      <p style={{ color: STATUS_HEX.serious }}>{p.overdueReal} Overdue</p>
      <p style={{ color: STATUS_HEX.critical }}>{p.noPlanReal} No Plan</p>
      <p style={{ color: STATUS_HEX.good }}>{p.inRemediationReal} In Remediation</p>
    </div>
  );
}

export function FailedControlRemediationWidget() {
  // Drawer showing the full item list — either every failed control (from "View More")
  // or one application's items (from clicking a bar). null title means "all controls".
  const [drillView, setDrillView] = useState<{ title: string | null; rows: RemediationItem[] } | null>(null);
  const [drillSearch, setDrillSearch] = useState("");
  const [data, setData] = useState<SystemOwnerRemediationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchRemediationStatus()
      .then((res) => setData(res?.data ?? null))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load remediation status"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { appSummaries, totals } = useMemo(
    () => buildRemediationStatus(data?.applications ?? []),
    [data],
  );

  const showSkeleton = loading && !data;
  const showError = !!error && !data;

  // Sourced from each app's own totals items (not a flatten of ssps[]) per the API contract.
  const allItems = sortRemediationItems(appSummaries.flatMap((a) => a.items));
  // Chart shows the worst-accountability apps only; "View More" has the full list.
  const CHART_APP_LIMIT = 5;
  const appBars = buildAppBars(appSummaries).slice(0, CHART_APP_LIMIT);

  // Calculate dynamic x-axis max based on actual data max, rounded up to nearest 50
  const xAxisMax = useMemo(() => {
    if (appBars.length === 0) return 100;
    const max = Math.max(...appBars.map((bar) => bar.total));
    return Math.ceil(max / 50) * 50;
  }, [appBars]);

  const openAppDetail = (applicationName: string) => {
    const app = appSummaries.find((a) => a.applicationName === applicationName);
    setDrillSearch("");
    setDrillView({ title: applicationName, rows: sortRemediationItems(app?.items ?? []) });
  };
  const openAllDetail = () => {
    setDrillSearch("");
    setDrillView({ title: null, rows: allItems });
  };

  const drillRows = drillView?.rows ?? [];
  const filteredDrillRows = drillSearch.trim()
    ? drillRows.filter((r) => {
        const q = drillSearch.trim().toLowerCase();
        return (
          r.controlId?.toLowerCase().includes(q) ||
          r.controlTitle?.toLowerCase().includes(q) ||
          r.applicationName?.toLowerCase().includes(q) ||
          r.sspName?.toLowerCase().includes(q)
        );
      })
    : drillRows;

  const columns: Column<RemediationItem>[] = [
    {
      key: "control",
      header: "Failed Control",
      align: "left",
      headerAlign: "left",
      render: (r) => (
        <div className="min-w-0">
          <p className="text-sm font-bold text-foreground">{r.controlId ?? "(unassigned)"}</p>
          {r.controlTitle && <p className="text-xs text-muted-foreground">{r.controlTitle}</p>}
        </div>
      ),
    },
    {
      key: "app",
      header: "Application / SSP",
      align: "left",
      headerAlign: "left",
      render: (r) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{r.applicationName}</p>
          <p className="text-xs text-muted-foreground">{r.sspName}</p>
        </div>
      ),
    },
    { key: "failedSince", header: "Failed Since", render: (r) => <span className="text-sm text-foreground/80">{failedSinceLabel(r.failedSince)}</span> },
    { key: "status", header: "POA&M Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "remediation", header: "Remediation", align: "left", headerAlign: "left", render: (r) => <RemediationCell item={r} /> },
  ];

  return (
    <Card className="flex h-[520px] flex-col">
      <div className="mb-2 flex items-start justify-between gap-2">
        <SectionTitle
          title="Failed Control Remediation Status"
          subtitle="Monitors remediation progress for failed controls through their associated POA&M lifecycle."
        />
        <div className="flex shrink-0 items-center gap-3">
          <Tip
            label={
              <span className="block max-w-[260px]">
                No Plan: a failed control with no POA&M item yet — needs one created. In Remediation: a POA&M item
                exists, has an assignee, and isn't overdue. Overdue: the POA&M item's scheduled completion date has
                passed.
              </span>
            }
          >
            <AlertCircle className="size-4 shrink-0 text-muted-foreground" />
          </Tip>
          <button
            onClick={openAllDetail}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-indigo-500/50 px-2.5 py-1 text-xs font-semibold text-foreground transition-colors hover:border-indigo-500 hover:bg-indigo-500/10"
          >
            View More <ArrowRight className="size-3.5" />
          </button>
        </div>
      </div>

      {showSkeleton ? (
        <div className="min-h-0 flex-1 pt-2">
          <SkeletonList rows={5} />
        </div>
      ) : showError ? (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <EmptyState
            icon={AlertCircle}
            title="Couldn't load this widget"
            description={error!}
            action={
              <button
                onClick={load}
                className="rounded-lg border border-indigo-500/50 px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-indigo-500 hover:bg-indigo-500/10"
              >
                Retry
              </button>
            }
          />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 gap-4">
          <div className="min-h-0 w-full flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={appBars} layout="vertical" margin={{ top: 4, right: 32, left: 4, bottom: 4 }} barCategoryGap="30%">
                <CartesianGrid horizontal={false} strokeDasharray="3 3" className="stroke-border" />
                <XAxis type="number" domain={[0, xAxisMax]} allowDecimals={false} tick={{ fontSize: 11, fill: "currentColor" }} className="text-foreground" axisLine={{ stroke: "#94a3b8", strokeOpacity: 0.8 }} tickLine={false} />
                <YAxis type="category" dataKey="applicationName" width={100} tick={{ fontSize: 11, fill: "currentColor" }} className="text-foreground" axisLine={{ stroke: "#94a3b8", strokeOpacity: 0.8 }} tickLine={false} />
                <Tooltip content={<AppBarTooltip />} cursor={{ fill: "rgba(100,116,139,0.12)" }} />
                <Bar dataKey="overdue" name="Overdue" stackId="status" fill={`${STATUS_HEX.serious}26`} stroke={STATUS_HEX.serious} strokeWidth={1.5} maxBarSize={22} shape={makeSegmentShape("overdue")} cursor="pointer" onClick={(d: { applicationName?: string }) => d?.applicationName && openAppDetail(d.applicationName)}>
                  <LabelList dataKey="overdueReal" position="center" className="fill-foreground" fontSize={11} fontWeight={700} formatter={(v: number) => (v > 0 ? v : "")} verticalAnchor="middle" textAnchor="middle" style={{ pointerEvents: "none" }} />
                </Bar>
                <Bar dataKey="noPlan" name="No Plan" stackId="status" fill={`${STATUS_HEX.critical}26`} stroke={STATUS_HEX.critical} strokeWidth={1.5} maxBarSize={22} shape={makeSegmentShape("noPlan")} cursor="pointer" onClick={(d: { applicationName?: string }) => d?.applicationName && openAppDetail(d.applicationName)}>
                  <LabelList dataKey="noPlanReal" position="center" className="fill-foreground" fontSize={11} fontWeight={700} formatter={(v: number) => (v > 0 ? v : "")} verticalAnchor="middle" textAnchor="middle" style={{ pointerEvents: "none" }} />
                </Bar>
                <Bar
                  dataKey="inRemediation"
                  name="In Remediation"
                  stackId="status"
                  fill={`${STATUS_HEX.good}26`}
                  stroke={STATUS_HEX.good}
                  strokeWidth={1.5}
                  maxBarSize={22}
                  shape={makeSegmentShape("inRemediation")}
                  cursor="pointer"
                  onClick={(d: { applicationName?: string }) => d?.applicationName && openAppDetail(d.applicationName)}
                >
                  <LabelList dataKey="inRemediationReal" position="center" className="fill-foreground" fontSize={11} fontWeight={700} formatter={(v: number) => (v > 0 ? v : "")} verticalAnchor="middle" textAnchor="middle" style={{ pointerEvents: "none" }} />
                </Bar>
                {/* Invisible marker at a fixed tiny (never-null) value so the "Total" label always sits at the
                    true end of the stack — attaching it to inRemediation would hide it whenever that bucket is 0. */}
                <Bar dataKey="totalMarker" stackId="status" fill="transparent" stroke="none" legendType="none" tooltipType="none" isAnimationActive={false}>
                  <LabelList dataKey="total" position="right" className="fill-foreground" fontSize={11} fontWeight={700} style={{ pointerEvents: "none" }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Side legend — status + count, swatch matches the bar's outline style (translucent fill, colored border). */}
          <div className="flex w-[165px] shrink-0 flex-col justify-center gap-2.5">
            {REMEDIATION_STATUS_ORDER.map((status) => {
              const count = totals[status];
              const hex = STATUS_META[status].hex;
              return (
                <div key={status} className="flex items-center gap-1.5">
                  <span className="size-2.5 shrink-0 rounded-[3px]" style={{ background: `${hex}26`, border: `1.5px solid ${hex}` }} />
                  <span className="min-w-0 flex-1 text-xs font-medium text-foreground/80">{STATUS_META[status].label}</span>
                  <span className="shrink-0 text-xs font-bold tabular-nums text-foreground">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Sheet open={drillView !== null} onOpenChange={(o) => { if (!o) setDrillView(null); }}>
        <SheetContent
          side="right"
          className="flex h-full max-h-screen w-[96vw] flex-col gap-0 overflow-hidden border-l border-border bg-background p-0 sm:max-w-[1000px]"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div key={drillView?.title ?? "all"} className="flex h-full min-h-0 flex-1 flex-col animate-in fade-in-0 slide-in-from-right-4 duration-300 ease-out">
            <div className="shrink-0 border-b border-border p-6">
              <h2 className="text-xl font-bold text-foreground">{drillView?.title ?? "All Failed Controls"}</h2>
              <p className="mt-1 text-sm text-muted-foreground">Exact POA&M item, status, and due/overdue date per failed control.</p>
              <div className="relative mt-3 max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground/50" />
                <input
                  type="text"
                  placeholder="Search by control, application, or SSP"
                  value={drillSearch}
                  onChange={(e) => setDrillSearch(e.target.value)}
                  className="h-[34px] w-full rounded-lg border border-border bg-muted/40 pl-8 pr-3 text-[13px] outline-none transition-all placeholder:text-muted-foreground/40 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                />
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <DataTable
                columns={columns}
                data={filteredDrillRows}
                rowKey={(r) => r.poamItemId}
                emptyText={drillSearch.trim() ? "No failed controls match your search" : "No failed controls to show"}
                hidePageButtons
                compact
              />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </Card>
  );
}
